/****************************************************************************
 * Copyright 2021 EPAM Systems
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ***************************************************************************/

import styled from '@emotion/styled';
import { HTMLInputTypeAttribute } from 'react';

interface Props {
  value: string | number;
  id: string;
  onChange: (value: string) => void;
  label?: string;
  type?: HTMLInputTypeAttribute;
  min?: number;
  max?: number;
  className?: string;
  inputClassName?: string;
}

const Label = styled.label(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginRight: '8px',
  color: theme.ketcher.color.text.primary,
  fontSize: '12px',
}));

const Input = styled.input(({ theme }) => ({
  height: '24px',
  padding: '3px 7px',
  border: `1px solid ${theme.ketcher.color.input.border.regular}`,
  fontSize: '14px',
  borderRadius: '4px',
  backgroundColor: theme.ketcher.color.input.background.primary,
  color: theme.ketcher.color.text.primary,
  outline: 'transparent',
  width: '164px',
  letterSpacing: 'normal',

  '&:active, &:focus': {
    color: theme.ketcher.color.input.text.active,
  },

  '&:hover': {
    border: `1px solid ${theme.ketcher.color.input.border.hover}`,
  },
}));

export const TextInputField = ({
  value,
  id,
  onChange,
  label,
  type,
  className,
  inputClassName,
  ...rest
}: Props) => {
  const handleChange = (event) => {
    onChange(event.target.value);
  };

  return (
    <Label htmlFor={id} className={className}>
      {label && <span>{label}</span>}
      <Input
        type={type || 'text'}
        id={id}
        value={value}
        className={inputClassName}
        onChange={handleChange}
        {...rest}
      />
    </Label>
  );
};
